import React, { useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

// One reported item's moderation verdict controls, embedded per-row in the queue.
// The moderator picks a verdict about the CONTENT — keep it, ghost/restore it, or
// remove it — presented as a short choice list rather than a row of buttons. Which
// verdicts show adapts to the item's current visibility, and the "Keep" verdict
// (records reviewed_ok, no visibility change) only appears while there are unreviewed
// reports to clear. Verdict wording matches the item's status: a visible item is
// "Ghost it", a ghosted one is "Keep ghosted" (a shadowban — hidden from everyone but
// the author). Remove is the terminal, two-click-confirmed soft-delete.
//
// Posts to the token-authed reported-content API (skip_forgery_protection, so only
// the bearer header is needed). Every verdict passes reviewed_through = the item's
// last_reported_at high-water (rendered in offset form by the view, passed as-is), so
// a report that arrived after this row was fetched still counts (no swallow).
function ModerationActionPanel({ item, onActionComplete }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const status = item.current_status;
  const isVisible = status === "visible";
  // A moderation-hide GHOSTS the content, so "ghosted" is the only moderation-hidden
  // state the hub restores. "pending_manual_review" is a post awaiting its FIRST
  // approval (never moderation-hidden) — approving it is the feed-post approval
  // queue's job, and "restoring" it here would publish never-approved content — so
  // it is treated like unprocessed: no transition, only Keep / Remove.
  const isGhosted = status === "ghosted";
  const isHidden = isGhosted || status === "pending_manual_review";
  const isDeleted = status === "deleted";
  const hasUnreviewed = Number(item.unreviewed_report_count) > 0;

  const reviewedThrough = item.last_reported_at || undefined;

  const performAction = async (action) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/admin/api/reported_content/${action}`, {
        method: "POST",
        headers: { ...authenticatedHeadersHash(), "Content-Type": "application/json" },
        body: JSON.stringify({
          reportable_type: item.reportable_type,
          reportable_id: item.reportable_id,
          reviewed_through: reviewedThrough,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || `Action failed (${res.status})`);
        return;
      }
      onActionComplete?.();
    } catch {
      setError("Network error — please retry");
    } finally {
      setBusy(false);
      setConfirmingRemove(false);
    }
  };

  // "Keep" clears the reports without changing visibility (mark_reviewed →
  // reviewed_ok); it only appears while there are unreviewed reports to clear, and
  // its wording tracks the current state. A ghosted item uses "ghost" terminology to
  // match its status (a shadowban — hidden from everyone but the author); a
  // pending-review item is merely hidden awaiting its first approval, not ghosted.
  let keepPresentation = { label: "Keep — leave as is", icon: "fa-check", variant: "outline-secondary" };
  if (isVisible) {
    keepPresentation = { label: "Keep — content is fine", icon: "fa-check", variant: "outline-success" };
  } else if (isGhosted) {
    keepPresentation = { label: "Keep ghosted", icon: "fa-ghost", variant: "outline-warning" };
  } else if (isHidden) {
    keepPresentation = { label: "Keep hidden", icon: "fa-eye-slash", variant: "outline-warning" };
  }
  const keepVerdict = { key: "keep", action: "mark_reviewed", ...keepPresentation };

  // The visibility transition, only where one is meaningful: hide a visible item, or
  // restore a moderation-ghosted one. A not-yet-published item (pending first review /
  // unprocessed / catch-all) gets NO transition here — approving a pending post belongs
  // in the feed-post approval queue, not the moderation hub, and "restoring" it would
  // wrongly publish content that was never approved.
  let transitionVerdict = null;
  if (isVisible) {
    transitionVerdict = {
      key: "hide",
      action: "hide",
      label: "Ghost it — hide from others",
      icon: "fa-ghost",
      variant: "outline-warning",
    };
  } else if (isGhosted) {
    transitionVerdict = {
      key: "restore",
      action: "re_approve",
      label: "Restore — make it visible",
      icon: "fa-check",
      variant: "outline-success",
    };
  }

  const removeVerdict = {
    key: "remove",
    action: "remove",
    label: "Remove it",
    icon: "fa-triangle-exclamation",
    variant: "outline-danger",
    confirm: true,
  };

  // Visible: [Keep?] Ghost Remove. Ghosted: Restore [Keep ghosted?] Remove.
  // Pending / unprocessed / catch-all: [Keep?] Remove (no transition). Deleted: none.
  const ordered = isVisible
    ? [hasUnreviewed && keepVerdict, transitionVerdict, removeVerdict]
    : [transitionVerdict, hasUnreviewed && keepVerdict, removeVerdict];
  const verdicts = isDeleted ? [] : ordered.filter(Boolean);

  const renderVerdict = (v) => {
    const confirming = v.confirm && confirmingRemove;
    return (
      <button
        key={v.key}
        type="button"
        className={`btn btn-sm text-start ${confirming ? "btn-danger" : `btn-${v.variant}`}`}
        disabled={busy}
        onClick={() => (v.confirm && !confirmingRemove ? setConfirmingRemove(true) : performAction(v.action))}
      >
        <i className={`fa-solid ${confirming ? "fa-triangle-exclamation" : v.icon} me-2`} />
        {confirming ? "Confirm remove" : v.label}
      </button>
    );
  };

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 220 }}>
      {verdicts.length > 0 && <div className="d-flex flex-column gap-1">{verdicts.map(renderVerdict)}</div>}
      {error && <div className="text-danger small">{error}</div>}
    </div>
  );
}

ModerationActionPanel.propTypes = {
  item: PropTypes.shape({
    reportable_type: PropTypes.string.isRequired,
    reportable_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    current_status: PropTypes.string,
    unreviewed_report_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    last_reported_at: PropTypes.string,
  }).isRequired,
  onActionComplete: PropTypes.func,
};

export default ModerationActionPanel;
